/**
 * ============================================================================
 * 클라이언트 앱 ApplicationConfig
 * ============================================================================
 *
 * [환경]
 * - Angular Core: 19.0.3
 * - 렌더링 모드: SSR (Server-Side Rendering) + Hydration
 * - Change Detection: Zone-based (eventCoalescing)
 *
 * [SSR 설정 상태 — 최신: 2026-07-03]
 * ✅ provideClientHydration(withEventReplay())
 *    - 서버에서 렌더링된 HTML을 클라이언트가 이어받아 hydration
 *    - withEventReplay: hydration 전 사용자 이벤트도 정상 처리
 * ✅ provideHttpClient(withFetch(), withInterceptors([httpInterceptor]))
 *    - withFetch: SSR 환경에서 XHR 미지원 문제 해결
 *    - SSR에서 HTTP 요청은 PendingTasks에 자동 등록되어 whenStable() 대기
 * ✅ provideZoneChangeDetection({ eventCoalescing: true })
 *    - 같은 이벤트 사이클 내 다중 변경 감지를 한 번으로 합침
 *
 * [주의사항]
 * - SSR 환경(Node.js)에는 localStorage / window / document / Kakao 전역 없음
 *   → 브라우저 API 사용 시 반드시 isPlatformBrowser() 가드 필요
 * - setTimeout / setInterval / Promise.resolve().then(...)은 SSR 대기에 포함 안 됨
 *   → Angular SSR이 자동으로 대기하는 것은 HttpClient(fetch) 요청만
 *
 * [히스토리]
 * - 2026-07-03: withEventReplay() 추가 — SEO/네이버 색인 개선 (#2b54301)
 * - 2026-07-03: 사이트 제목 "로컬커뮤니티 플랫폼 남도랑(namdorang)" 통일 (#2b54301)
 * ============================================================================
 */
import {
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
  PLATFORM_ID,
  provideZoneChangeDetection,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import localeKo from "@angular/common/locales/ko";
import { provideRouter, TitleStrategy } from "@angular/router";
import { appRoutes } from "./app.routes";
import {
  provideClientHydration,
  withEventReplay,
} from "@angular/platform-browser";
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideToastr } from "ngx-toastr";
import { provideMarkdown } from "ngx-markdown";
import { environment } from "../environments/environment";
import { ApiConfiguration, ApiModule } from "@namdorang/api-client";
import { httpInterceptor } from "./libs/http.interceptor";
import { registerLocaleData } from "@angular/common";
import { UPLOAD_SERVICE_PROVIDER_TOKEN } from "@client";
import { UploadService } from "./services/upload.service";

registerLocaleData(localeKo, "ko");

type GlobalWithProcess = typeof globalThis & {
  process?: {
    env?: Record<string, string | undefined>;
  };
};

function getServerApiRootUrl() {
  const env = (globalThis as GlobalWithProcess).process?.env;

  return (
    env?.["NAMDORANG_SERVER_API_URL"] ||
    env?.["API_BASE_URL"] ||
    "http://server:3000"
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(withEventReplay()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withInterceptors([httpInterceptor]), withFetch()),
    provideToastr(),
    { provide: LOCALE_ID, useValue: "ko" },
    provideAnimations(),
    provideMarkdown(),
    provideRouter(appRoutes),
    importProvidersFrom(ApiModule),
    {
      provide: ApiConfiguration,
      useFactory: (platformId: object) => ({
        rootUrl: isPlatformBrowser(platformId)
          ? environment.baseUrl
          : getServerApiRootUrl(),
      }),
      deps: [PLATFORM_ID],
    },
    {
      provide: UPLOAD_SERVICE_PROVIDER_TOKEN,
      useClass: UploadService,
    },
  ],
};
